// src/components/app/app-utils/txStats.js
//
// Pure utilities for summarizing transactions.
// Relies on inferAccountDelta for account-aware math when an account is provided.

import { inferAccountDelta } from "./txDelta";

/** Return integer number of whole days between two ISO yyyy-mm-dd (inclusive). */
export function daysInRange({ from, to }) {
  if (!from || !to) return 0;
  const a = Date.parse(from);
  const b = Date.parse(to);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  // inclusive days: floor diff / 1d + 1
  const ONE_DAY = 24 * 60 * 60 * 1000;
  return Math.max(1, Math.floor((b - a) / ONE_DAY) + 1);
}

/** Group items by ISO date (yyyy-mm-dd). Invalid/missing dates are ignored for bucketing. */
export function bucketByDay(items, { getDate = (r) => r?.date } = {}) {
  const map = new Map();
  for (const it of items || []) {
    const raw = String(getDate(it) || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) continue;
    if (!map.has(raw)) map.set(raw, []);
    map.get(raw).push(it);
  }
  // Sort keys ascending (oldest -> newest) for charting
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

/**
 * Sum deltas.
 * If accountId is provided -> use inferAccountDelta(tx, accountId) (account-aware: transfers directional).
 * Else -> use signed amount directly (to avoid double-counting transfers in multi-account views).
 */
export function sumDeltas(items, accountId) {
  let inflow = 0;
  let outflow = 0;
  for (const tx of items || []) {
    let d;
    if (accountId) {
      d = inferAccountDelta(tx, accountId);
    } else {
      const n = Number(tx?.amount || 0);
      d = Number.isFinite(n) ? n : 0;
    }
    if (d > 0) inflow += d;
    else if (d < 0) outflow += -d;
  }
  const net = inflow - outflow;
  return { inflow, outflow, net };
}

/** Build a simple daily net series for a sparkline (oldest -> newest). */
export function dailySeriesFromBuckets(dayPairs, accountId) {
  const labels = [];
  const values = [];
  for (const [iso, txs] of dayPairs || []) {
    const { inflow, outflow, net } = sumDeltas(txs, accountId);
    labels.push(iso);
    values.push(net);
  }
  return { labels, values };
}

/** Compact numeric string; keep it simple (no currency symbol here). */
export function formatCompact(n, opts = {}) {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "0";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  const digits = opts.maximumFractionDigits ?? 1;
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(digits)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(digits)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(digits)}k`;
  return `${sign}${abs.toFixed(Math.min(digits, 2))}`;
}
/** Simple centered or trailing moving average (trailing by default). */
export function movingAverage(values = [], win = 7, { mode = "trailing" } = {}) {
  const n = Math.max(1, Math.floor(win));
  if (!values.length || n <= 1) return Array.from(values);
  const out = [];
  if (mode === "centered") {
    const half = Math.floor(n / 2);
    for (let i = 0; i < values.length; i++) {
      const s = Math.max(0, i - half);
      const e = Math.min(values.length, i + half + 1);
      const slice = values.slice(s, e);
      out.push(slice.reduce((a, b) => a + b, 0) / slice.length);
    }
    return out;
  }
  // trailing
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
    if (i >= n) sum -= values[i - n];
    out.push(sum / Math.min(i + 1, n));
  }
  return out;
}

/** Build daily inflow/outflow arrays (oldest -> newest) from day buckets. */
export function dailyInflowOutflowFromBuckets(dayPairs, accountId) {
  const labels = [];
  const inflows = [];
  const outflows = [];
  for (const [iso, txs] of dayPairs || []) {
    let inflow = 0;
    let outflow = 0;
    for (const tx of txs) {
      let d;
      if (accountId) d = inferAccountDelta(tx, accountId);
      else {
        const n = Number(tx?.amount || 0);
        d = Number.isFinite(n) ? n : 0;
      }
      if (d > 0) inflow += d;
      else if (d < 0) outflow += -d;
    }
    labels.push(iso);
    inflows.push(inflow);
    outflows.push(outflow);
  }
  return { labels, inflows, outflows };
}

// ---- New: period helpers (week/month) ------------------------------

function isoWeekKey(dateObj) {
  // ISO week: Mon-based weeks
  const d = new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()));
  // Thursday in current week decides the year
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  const ww = String(weekNo).padStart(2, "0");
  return `${d.getUTCFullYear()}-W${ww}`;
}

// Choose a sensible default period from range length
export function choosePeriod(rangeDays = 0) {
  if (rangeDays <= 31) return "day";
  if (rangeDays <= 180) return "week";
  return "month";
}

// Aggregate inflow/outflow by day/week/month (account-aware if accountId provided)
export function inflowOutflowByPeriod(items = [], { period = "week", accountId } = {}) {
  // 1) bucket by day first (oldest -> newest)
  const byDay = new Map(); // iso -> { inflow, outflow }
  for (const [iso, txs] of bucketByDay(items)) {
    let inflow = 0, outflow = 0;
    for (const tx of txs) {
      let d;
      if (accountId) {
        d = inferAccountDelta(tx, accountId);
      } else {
        const n = Number(tx?.amount || 0);
        d = Number.isFinite(n) ? n : 0;
      }
      if (d > 0) inflow += d;
      else if (d < 0) outflow += -d;
    }
    byDay.set(iso, { inflow, outflow });
  }

  if (period === "day") {
    const labels = Array.from(byDay.keys()).sort();
    const inflows = labels.map(iso => byDay.get(iso)?.inflow || 0);
    const outflows = labels.map(iso => byDay.get(iso)?.outflow || 0);
    return { labels, inflows, outflows };
  }

  // week (use Monday week start)
  const weekStartISO = (iso) => {
    const d = new Date(`${iso}T00:00:00Z`);
    const day = d.getUTCDay(); // 0..6 (Sun..Sat)
    const delta = (day + 6) % 7; // go back to Monday
    d.setUTCDate(d.getUTCDate() - delta);
    const z = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    return z.toISOString().slice(0, 10);
  };

  const buckets = new Map(); // key -> { inflow, outflow, lastISO }
  const keyFn = period === "week"
    ? (iso) => weekStartISO(iso)
    : (iso) => iso.slice(0, 7); // month: "YYYY-MM"

  for (const iso of Array.from(byDay.keys()).sort()) {
    const key = keyFn(iso);
    const prev = buckets.get(key) || { inflow: 0, outflow: 0, lastISO: "" };
    const cur = byDay.get(iso) || { inflow: 0, outflow: 0 };
    const next = {
      inflow: prev.inflow + cur.inflow,
      outflow: prev.outflow + cur.outflow,
      lastISO: iso, // keep last day in that bucket for label ordering
    };
    buckets.set(key, next);
  }

  const ordered = Array.from(buckets.entries())
    .sort((a, b) => a[1].lastISO.localeCompare(b[1].lastISO));

  const labels = ordered.map(([k]) => k);
  const inflows = ordered.map(([, v]) => v.inflow);
  const outflows = ordered.map(([, v]) => v.outflow);
  return { labels, inflows, outflows };
}

